import { ArrowBack, PersonAddAlt1Rounded } from "@mui/icons-material";
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import MainLayout from "../../../../layouts/main/main_layout";
import { useFeedback } from "../../../../providers/feedback/feeedback";
import { RoleList } from "../../model/admin_roles";
import { addClient, getRoles } from "../../../../services/client_mapping/client_mapping";
import { useQuery } from "react-query";
import { queryClient } from "../../../../providers/query/query";
import { greyEnabled, purple, purpleLight, black } from "../../../../theme/colors";
import { DEFAULT_ORG_ID } from "../../../../constants";
import useUser from "../../../../providers/user/user_provider";
import { useSelectedOrgStore } from "../../../../store/use-selected-org-store";
import OrgSelector from "../../../../components/org_selector/org_selector";
export default function AddNewClient({ modifyFlow }: { modifyFlow: boolean }) {
  const navigate = useNavigate();
  const [adminName, setAdminName] = useState("");
  const [adminEmailId, setAdminEmailId] = useState("");
  const [adminRole, setAdminRole] = useState<RoleList>({} as RoleList);
  const [roles, setRoles] = useState<RoleList[]>([]);
  const [error, setError] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const { showSnackbar } = useFeedback();
  const { userPermissions } = useUser();
  const { selectedOrgId } = useSelectedOrgStore();
  // A super-admin picking an org below creates the user there; otherwise
  // fall back to the caller's own org, and only as a last resort (e.g.
  // permissions not loaded yet) to DEFAULT_ORG_ID.
  const targetOrgId = selectedOrgId ?? userPermissions?.orgId ?? DEFAULT_ORG_ID;


  const { data: roleList } = useQuery(
    ["get-Roles-add-clients", targetOrgId],
    async () => {
      let response = await getRoles({ page: 1, size: 1000, module: 'all', keyword: "", orgId: targetOrgId });
      return response.data.data;
    }
  );

  // A role from a previously-selected org is no longer valid once the
  // target org changes — roles are org-private.
  useEffect(() => {
    setAdminRole({} as RoleList);
  }, [targetOrgId]);

  const addAdminApiCall = () => {
    setSubmitting(true);
    addClient({ clientEmailId: adminEmailId, clientName: adminName, moduleId: adminRole.id, orgId: targetOrgId })
      .then((res) => {
        showSnackbar("Admin Added Successfully");
        queryClient.invalidateQueries({ queryKey: ["client-List"] });
        navigate('/clientMapping')
      })
      .catch((e) => {
        if (e.response.status === 445 && e.response.data.error.code === 445) {
          showSnackbar("User already exist !");
        } else if (
          e.response.status === 403 &&
          e.response.data.error.code === 403
        ) {
          showSnackbar("User don't have permission");
        } else if (e?.response?.data?.error?.message) {
          showSnackbar(e?.response?.data?.error?.message);

        } else {
          showSnackbar("Something went wrong");
        }
      })
      .finally(() => setSubmitting(false));
  };



  const submitForm = () => {
    const emailRegex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,4}$/;
    if (emailRegex?.test(adminEmailId)) {
      addAdminApiCall?.();
    } else {
      setError(true);
    }
  };

  return (
    <MainLayout>
      <Box>
        <Box display="flex" alignItems="center" gap={1}>
          <IconButton onClick={() => navigate(-1)}>
            <ArrowBack />
          </IconButton>
          <Box display="flex" alignItems="center" gap={1.5}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 40,
                height: 40,
                borderRadius: 2,
                bgcolor: purpleLight,
                flexShrink: 0,
              }}
            >
              <PersonAddAlt1Rounded sx={{ color: purple, fontSize: 22 }} />
            </Box>
            <Box>
              <Typography variant="h5" fontWeight={700} color={black}>
                Add New Client
              </Typography>
              <Typography variant="body2" color={greyEnabled}>
                Map a new admin to a role and its module access.
              </Typography>
            </Box>
          </Box>
        </Box>

        <Card
          elevation={0}
          sx={{ width: '100%', maxWidth: 600, mt: 2, borderRadius: 3, border: '1px solid #ECECEC' }}
        >
          <CardContent sx={{ p: { xs: 2.5, sm: 4 } }}>
            <Box display="flex" flexDirection="column">
                  <Box>
                    <OrgSelector label="Organisation (defaults to your own)" fullWidth lockToOwnOrg platformDefaultsToOwnOrg />
                  </Box>
                  <Box mt={3}>
                    <TextField
                      fullWidth
                      label="Admin Name"
                      placeholder="Enter Admin name"
                      value={adminName}
                      onChange={(event) => {
                        const filtered = event.target.value.replace(/[^A-Za-z\s]/g, "");
                        if (filtered !== ' ')
                          setAdminName(filtered);
                      }}
                      inputProps={{ maxLength: 40 }}
                      required
                    />
                  </Box>
                  <Box mt={3}>
                    <TextField
                      fullWidth
                      label="Email ID"
                      placeholder="Enter Email id"
                      value={adminEmailId}
                      onChange={(event) => {
                        if (event.target.value !== ' ') {
                          setAdminEmailId(event?.target.value.trim());
                        }
                        setError(false)
                      }}
                      required
                    />
                    {error && (
                      <FormHelperText sx={{ color: "red", m: 0 }}>
                        Please Enter valid Email
                      </FormHelperText>
                    )}
                  </Box>
                  <Box mt={3}>
                    <FormControl fullWidth>
                      <InputLabel id="role-label">
                        Select Role<span style={{ color: "#D14C2E" }}>*</span>
                      </InputLabel>
                      <Select
                        labelId="role-label"
                        label="Select an Option"
                        fullWidth
                        placeholder="Select an Option"
                        value={adminRole.role}
                        MenuProps={{
                          PaperProps: {
                            elevation: 0,
                            sx: {
                              mt: 0.5,
                              borderRadius: 2,
                              border: "1px solid #ECECEC",
                              boxShadow: "0px 12px 28px -8px rgba(27, 0, 27, 0.18)",
                            },
                          },
                        }}
                      >
                        {roleList?.list?.map((role: RoleList) => (
                          <MenuItem
                            onClick={() => {
                              setAdminRole(role);
                            }}
                            value={role.role}
                            className={role.role}
                          >
                            {role.role}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>

                  <Box
                    display="flex"
                    flexDirection="row"
                    justifyContent="space-between"
                    mt={4}
                  >
                    <Button
                      onClick={() => navigate(-1)}
                      variant="outlined"
                      sx={{
                        borderColor: purple,
                        color: purple,
                        borderRadius: 2,
                        textTransform: "none",
                        fontWeight: 600,
                        minWidth: 150,
                        "&:hover": { borderColor: purple, bgcolor: purpleLight },
                      }}
                    >
                      Cancel
                    </Button>

                    <Button
                      onClick={submitForm}
                      disabled={
                        submitting || !(adminName && Object?.keys?.(adminRole)?.length)
                      }
                      variant="contained"
                      disableElevation
                      startIcon={submitting ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : undefined}
                      sx={{
                        minWidth: 150,
                        bgcolor: purple,
                        borderRadius: 2,
                        textTransform: "none",
                        fontWeight: 600,
                        "&:hover": { bgcolor: "#7A2074" },
                      }}
                    >
                      Add Client
                    </Button>
                  </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </MainLayout>
  );
}
